import { createBrowserHistory, type BrowserHistory, type Listener, type Location, type To } from 'history';

import { env } from 'config';

// `<Router basename>` covers <Link>/useNavigate; this shim covers MobX-store
// `routing.push(...)` which bypasses react-router. history@5 has no native
// basename — push/replace are wrapped, idempotent so useNavigate's prefixed
// calls aren't double-prefixed. `__PROTOTYPE_BASENAME__` is set pre-React.
// Read via `config` (not `import.meta.env`) so it resolves under Jest, which
// mocks the `config` module — `import.meta` would be a parse error there.

const prototypeBasename =
  env.REACT_APP_IS_PROTOTYPE === 'true'
    ? (window as Window & { __PROTOTYPE_BASENAME__?: string }).__PROTOTYPE_BASENAME__
    : undefined;

const history: BrowserHistory = createBrowserHistory();

if (prototypeBasename) {
  const basename = prototypeBasename;
  const isPrefixed = (path: string) => path === basename || path.startsWith(`${basename}/`);
  const prefix = (to: To): To => {
    if (typeof to === 'string') {
      const splitAt = to.search(/[?#]/);
      const pathname = splitAt === -1 ? to : to.slice(0, splitAt);
      const tail = splitAt === -1 ? '' : to.slice(splitAt);
      return !pathname || isPrefixed(pathname) ? to : `${basename}${pathname}${tail}`;
    }
    return !to.pathname || isPrefixed(to.pathname) ? to : { ...to, pathname: `${basename}${to.pathname}` };
  };

  const { push, replace } = history;
  history.push = (to, state) => push.call(history, prefix(to), state);
  history.replace = (to, state) => replace.call(history, prefix(to), state);

  // The MobX routing store reads `history.location.pathname` directly and
  // doesn't know about basename, so strip it on read — otherwise sidebar
  // `pathname.startsWith('/schedules/')` checks fail under /p/<id>/...
  const stripPathname = (p: string): string => {
    if (p === basename) return '/';
    if (p.startsWith(`${basename}/`)) return p.slice(basename.length);
    return p;
  };
  const stripLocation = (loc: Location): Location => ({
    ...loc,
    pathname: stripPathname(loc.pathname),
  });

  const locationDesc = Object.getOwnPropertyDescriptor(history, 'location');
  if (locationDesc?.get) {
    const originalGet = locationDesc.get;
    Object.defineProperty(history, 'location', {
      configurable: true,
      get: () => stripLocation(originalGet.call(history)),
    });
  }

  const originalListen = history.listen.bind(history);
  history.listen = (listener: Listener) =>
    originalListen(({ action, location }) => listener({ action, location: stripLocation(location) }));
}

export const browserHistory = history;
